import { BaseConnector } from './Base';
import { StandardWalletAdapter } from '@solana/wallet-standard-wallet-adapter-base';
import SolanaWallet from './Wallets/SolanaWallet';
import { getWallets } from '@wallet-standard/app';
import type { Wallet as StandardWallet } from '@wallet-standard/base';
import {
	WalletAdapterNetwork,
	WalletReadyState,
	isWalletAdapterCompatibleStandardWallet,
} from '@solana/wallet-adapter-base';
import { IObservableArray, action, flow, makeAutoObservable, observable, reaction, when } from 'mobx';
import type ExplorerRootStore from '../ExplorerRootStore';
import {
	SolanaMobileWalletAdapter,
	createDefaultAddressSelector,
	createDefaultAuthorizationResultCache,
	createDefaultWalletNotFoundHandler,
} from '@solana-mobile/wallet-adapter-mobile';
import { CoinbaseWalletAdapter } from '@solana/wallet-adapter-coinbase';
import { Connection } from '@solana/web3.js';
import { WalletConnectWalletAdapter } from './WalletConnect/SolanaAdapter';

function wrapWalletsWithAdapters(wallets: readonly StandardWallet[]): SolanaWallet[] {
	return wallets
		.filter(isWalletAdapterCompatibleStandardWallet)
		.map((wallet) => new StandardWalletAdapter({ wallet }))
		.map((wallet) => new SolanaWallet(wallet));
}

export default class SVMConnector implements BaseConnector<SolanaWallet> {
	lastUsed: SolanaWallet | undefined = undefined;
	root: ExplorerRootStore;
	adapters: StandardWallet[] = [];
	private rawWallets: IObservableArray<SolanaWallet>;
	connections = observable.map<number, Connection>();

	readonly coinbase: SolanaWallet;
	readonly mobileWallet: SolanaWallet;
	readonly walletconnect: SolanaWallet;
	constructor(root: ExplorerRootStore) {
		this.root = root;

		this.walletconnect = new SolanaWallet(new WalletConnectWalletAdapter(root));
		this.coinbase = new SolanaWallet(new CoinbaseWalletAdapter());
		this.mobileWallet = new SolanaWallet(
			new SolanaMobileWalletAdapter({
				addressSelector: createDefaultAddressSelector(),
				appIdentity: this.root.configs.appIdentity,
				authorizationResultCache: createDefaultAuthorizationResultCache(),
				cluster: WalletAdapterNetwork.Mainnet,
				onWalletNotFound: createDefaultWalletNotFoundHandler(),
			}),
		);

		const { get, on } = getWallets();
		this.adapters = get().slice();
		this.rawWallets = observable(wrapWalletsWithAdapters(this.adapters));

		makeAutoObservable<SVMConnector, 'register' | 'unregister' | 'rawWallets'>(
			this,
			{ register: action, unregister: action, rawWallets: false, lastUsed: observable.ref },
			{ autoBind: true },
		);
		on('register', this.register);
		on('unregister', this.unregister);

		reaction(() => this.root.configs.svmChains, this.#resetConnections);
		when(() => true, this.#resetConnections);
	}

	get loading() {
		return Boolean(this.wallets.some((w) => w.connecting || w.disconnecting));
	}

	get wallets() {
		const wallets = this.rawWallets.filter((w) => w.adapter.readyState !== WalletReadyState.Unsupported);
		if (this.walletconnect.installed) {
			wallets.push(this.walletconnect);
		}
		if (this.coinbase.installed) {
			wallets.unshift(this.coinbase);
		} else {
			wallets.push(this.coinbase);
		}
		if (this.mobileWallet.installed) {
			wallets.unshift(this.mobileWallet);
		}
		return wallets;
	}

	connect = flow(function* (this: SVMConnector, w: SolanaWallet) {
		yield w.connect();
		this.lastUsed = w;
	});
	disconnect(w: SolanaWallet) {
		return w.disconnect();
	}

	private register = (...wallets: StandardWallet[]) => {
		wrapWalletsWithAdapters(wallets).map((w) => this.rawWallets.push(w));
	};

	private unregister = (...wallets: StandardWallet[]) => {
		wallets.forEach((w) => {
			const wallet = this.wallets.find((wallet) => 'wallet' in wallet.adapter && wallet.adapter.wallet === w);
			if (wallet) {
				this.rawWallets.remove(wallet);
			}
		});
	};

	#resetConnections = action(() => {
		const chains = this.root.configs.svmChains;
		this.connections.clear();
		for (const chain of chains) {
			this.connections.set(
				chain.chainId,
				new Connection(chain.externalRpcURL || chain.rpcURL, { commitment: 'confirmed' }),
			);
		}
	});
}
